import { qfRestGet } from './qfClient';

/**
 * Economic calendar over a date range.
 *
 * Served by QuantForge `GET /v1/calendar/economic/` (live 2026-08-06, QF #704).
 * Integration reference: `docs/plans/2026-08-06-qf-calendar-endpoints-LIVE.md`.
 *
 * ### One row per release — flat, never grouped
 *
 * Rows arrive exactly as published. A single release may appear several times
 * on the same day cut different ways — an inflation print as both MoM and YoY.
 * The server does not consolidate them, there is no `raw` parameter and no
 * pre-formatted `detail` string; grouping and formatting are the client's job.
 *
 * Because rows are never merged, `actual`/`estimate`/`previous` carry real
 * numbers on **every** row rather than only on unmerged ones.
 *
 * If a crowded day cell ever does need grouping:
 *
 *  ⚠️ Never group revision stages. `final`, `prel`, `flash` and `adv` look like
 *    qualifiers but are separate releases on separate dates — merging them
 *    shows the same event twice a month with no way to tell which is which.
 *    Only period qualifiers may collapse: `mom`, `yoy`, `qoq`, `m/m`, `y/y`,
 *    `s.a.`, `n.s.a.`
 *  ⚠️ A group takes the HIGHEST impact of its members.
 *
 * ### Filtering is server-side
 *
 * The underlying feed is global — only ~23% of rows are US (1,485 of 6,461
 * measured live), the rest UK, JP, DE, CA, AU and ~40 more — and skews heavily
 * low-impact (4,849 low / 1,300 medium / 312 high). Both are narrowed with
 * request parameters rather than after the fact; see `COUNTRY` and `IMPACT`.
 *
 * ⚠️ A wrong parameter name may not announce itself. If the server ignores an
 * unrecognised filter rather than rejecting it, the response is a perfectly
 * valid 200 carrying the unfiltered feed — Malaysian releases in the day cells
 * and no error anywhere. The tell is volume: a filtered month is tens of rows,
 * an unfiltered one is thousands. Check the count, not the status code.
 *
 * ⛔ Never fall back to filtering by title. String-matching country names or
 * keeping a title allowlist both silently drop real US releases, which is worse
 * than the noise they remove.
 *
 * ### Coverage
 *
 * Roughly T-30 back to T+120 forward, refreshed daily. Each refresh REPLACES
 * the window, so cancelled or rescheduled releases disappear correctly rather
 * than lingering.
 */

/** How much the tape tends to care. */
export type Impact = 'high' | 'medium' | 'low';

export interface EconomicEvent {
  /** ISO date in EASTERN terms, YYYY-MM-DD. */
  date: string;
  /** Release name, e.g. "ISM Manufacturing PMI". */
  title: string;
  /**
   * Eastern clock time, 24h, e.g. "08:30". Absent for all-day entries.
   *
   * ⚠️ ~1.3% of rows (Malaysia, Indonesia, Thailand, a few EU) use "00:00" as
   * an all-day sentinel rather than omitting the field. No US rows are
   * affected — US times are correct (08:30 CPI/NFP, 10:00 ISM, 14:00 FOMC).
   */
  time?: string;
  impact: Impact;
  /**
   * The figures. Real JSON numbers; the unit lives in `unit`.
   *
   * Optional fields are OMITTED from the response, never sent as null — test
   * with `!= null` or optional chaining rather than `=== null`.
   */
  actual?: number;
  estimate?: number;
  previous?: number;
  /** e.g. "%", "K", "M". Belongs with the figures above. */
  unit?: string;
}

export interface EconomicCalendar {
  events: EconomicEvent[];
  /** Window actually covered. Null when the server-side query failed. */
  from: string | null;
  to: string | null;
  /** When the source was last reached, ISO. Null on failure. */
  fetchedAt: string | null;
}

/**
 * Thrown when the endpoint reports failure through a null envelope.
 *
 * Deliberately NOT exported: no caller distinguishes this from any other
 * failure — React Query only needs the rejection to reach `isError`. The named
 * class earns its place in the stack trace, not in the module's surface.
 */
class EconomicCalendarUnavailableError extends Error {
  constructor() {
    super('QuantForge economic calendar is unavailable');
    this.name = 'EconomicCalendarUnavailableError';
  }
}

/**
 * Server-side filters.
 *
 * Both are sent on every request; neither is a user choice. The page is a US
 * trading calendar, and the low tier is several releases a day that crowd out
 * the cells carrying a CPI or an FOMC.
 *
 * ⚠️ Filtering here rather than in the client is what makes the counts honest.
 * The unfiltered feed is ~6,461 rows for a window, of which ~1,485 are US and
 * 312 are high impact — so a client-side filter would fetch twenty times the
 * data to draw the same grid, and `noReleases` would be measuring the wrong
 * set entirely.
 */
const COUNTRY = 'US';

/**
 * ⚠️ Comma-separated, NOT repeated params. If QF ever switches to
 * `?impact=high&impact=medium`, `qfRestGet`'s flat `Record<string, string>`
 * cannot express it and this needs a different call path.
 */
const IMPACT = 'high,medium';

/**
 * Economic releases between two dates, inclusive. Eastern calendar dates.
 *
 * ⛔ Failure is signalled by a NULL ENVELOPE, not a status code. The route
 * degrades rather than 500-ing, so BOTH of these arrive as HTTP 200:
 *
 *   • `events: []` with `from`/`to` populated  → nothing scheduled
 *   • `events: []` with `from`/`to`/`fetchedAt` all null → the query FAILED
 *
 * `events.length === 0` alone cannot tell them apart, so this function throws
 * on the null envelope. Without that check a failed fetch renders as "a quiet
 * week" — a confident, plausible, wrong answer — and callers relying on React
 * Query's `isSuccess` to mean "the month really is empty" would be misled.
 */
export async function fetchEconomicCalendar(
  fromIso: string,
  toIso: string
): Promise<EconomicCalendar> {
  return fetchCalendar<EconomicCalendar>('/v1/calendar/economic/', fromIso, toIso);
}

/**
 * One raw row a release absorbed, carrying its ORIGINAL title and its OWN
 * figures — `CPI MoM (Jul)` at 0.2%, not the release's headline 2.7%.
 *
 * Structurally identical to `EconomicEvent`; the alias exists so the field it
 * types reads as what it is at the call site.
 */
export type EconomicEventMember = EconomicEvent;

/**
 * A consolidated release: one row for what was published as several.
 *
 * The row's own `title`/`impact`/`time` are the ROLLUP — the release name, the
 * HIGHEST impact of its members, the EARLIEST time. Its figures come from the
 * highest-impact member alone, which is why `members` matters: on a CPI morning
 * the nine other cuts carry real numbers that the row does not show.
 */
export interface EconomicRelease extends EconomicEvent {
  /**
   * Every row folded into this one, always present and never empty — an
   * unmerged release carries itself as its single member, so this needs no
   * null check. `members.length > 1` is the "was actually merged" test.
   *
   * ℹ️ Nothing renders this today: the Overview shows title and time only, and
   * the Economic view is on the raw endpoint which has no `members` at all. It
   * is typed because it is what the endpoint sends — the alternative is that
   * the next person reads the backend to discover the detail exists.
   */
  members: EconomicEventMember[];
}

export interface EconomicReleaseCalendar extends Omit<EconomicCalendar, 'events'> {
  events: EconomicRelease[];
}

/**
 * Consolidated calendar — one row per RELEASE. Backs the Overview view.
 *
 * ⚠️ OPINIONATED, and deliberately so: the ten cuts of a CPI print arrive as a
 * single `CPI / Inflation Rate` row, and a short list of low-value releases
 * (CFTC positioning, the Treasury budget statement, the Conference Board
 * Leading Index) is dropped ENTIRELY rather than merged. Measured on prod for
 * 2026-08-12: 17 raw rows became 5.
 *
 * ⛔ The Economic view must keep calling `fetchEconomicCalendar`. That is the
 * unedited feed, and it is the only place the dropped releases still appear.
 *
 * The opinion lives on the server so Discord and the morning report read the
 * same grouping instead of each re-deriving it. To change what merges, change
 * `economic_calendar_groups.py` in QF — not this file.
 */
export async function fetchEconomicReleases(
  fromIso: string,
  toIso: string
): Promise<EconomicReleaseCalendar> {
  return fetchCalendar<EconomicReleaseCalendar>(
    '/v1/calendar/economic/releases/',
    fromIso,
    toIso
  );
}

/**
 * The shared request both calendars make: same params, same null-envelope
 * failure, different path and row shape.
 *
 * Written once rather than twice so the `COUNTRY`/`IMPACT` filters and the
 * degraded-response guard cannot drift apart between the two views — a
 * half-applied change to either is exactly the bug that would be hardest to
 * see, since both endpoints answer 200 whatever happens.
 */
async function fetchCalendar<T extends { from: string | null }>(
  path: string,
  fromIso: string,
  toIso: string
): Promise<T> {
  const body = await qfRestGet<T>(path, {
    from: fromIso,
    to: toIso,
    country: COUNTRY,
    impact: IMPACT,
  });

  if (body.from === null) {
    throw new EconomicCalendarUnavailableError();
  }

  return body;
}
